// Koło nastroju (patch-32): czysta matematyka obrotu — który wycinek jest pod wskaźnikiem, rozpęd po puszczeniu palca, dosunięcie do wycinka.
// Kąty w stopniach, dodatnie = zgodnie z ruchem wskazówek zegara. Wycinek `i` leży na kole w kącie `i * krok`; wskaźnik stoi w kącie 0 (u góry
// w układzie koła), więc przy obrocie koła o `angle` pod wskaźnikiem jest wycinek `-angle / krok`. Ekran ustawia kierunek wskaźnika obrotem całego koła.

export const mod = (a: number, n: number): number => ((a % n) + n) % n;
/** Kąt sprowadzony do przedziału [−180, 180). */
export const norm180 = (d: number): number => mod(d + 180, 360) - 180;
export const stepOf = (n: number): number => 360 / n;

/** Który wycinek (0…n−1) jest pod wskaźnikiem, gdy koło obrócono o `angle`. */
export const indexAt = (angle: number, n: number): number => mod(Math.round(-angle / stepOf(n)), n);

/** Obrót koła, który stawia wycinek `index` pod wskaźnikiem; spośród równoważnych (±360°) wybiera najbliższy `near`. */
export function angleFor(index: number, n: number, near = 0): number {
  const base = -index * stepOf(n);
  return base + 360 * Math.round((near - base) / 360) + 0; // „+ 0” zamienia −0 na 0
}

/** Najbliższy obrót, w którym jakiś wycinek leży dokładnie pod wskaźnikiem. */
export const snapAngle = (angle: number, n: number): number => angleFor(indexAt(angle, n), n, angle);

/** Numer „krawędzi” wycinka, którą koło minęło: zmiana o 1 = wskaźnik przeszedł do następnego wycinka (do tyknięć podczas kręcenia). */
export const edgeIndex = (angle: number, n: number): number => Math.floor((-angle + stepOf(n) / 2) / stepOf(n));
export const edgesCrossed = (from: number, to: number, n: number): number => Math.abs(edgeIndex(to, n) - edgeIndex(from, n));

/** Kąt punktu (x, y) wokół środka (cx, cy): 0 = nad środkiem, rośnie zgodnie z ruchem wskazówek zegara. */
export const pointerAngle = (x: number, y: number, cx: number, cy: number): number => (Math.atan2(x - cx, -(y - cy)) * 180) / Math.PI;

export interface AngleSample {
  /** Czas w ms. */
  t: number;
  /** Obrót koła w tej chwili (stopnie, bez zawijania do 360). */
  a: number;
}

export const MAX_SPIN = 2000; // °/s
/** Rozpęd tarcia: prędkość maleje o e^(−FRICTION·s) na sekundę; przebyta droga to ok. prędkość / FRICTION (900°/s ≈ 375°, ponad obrót). */
export const FRICTION = 2.4;
/** Poniżej tej prędkości kończymy rozpęd i dosuwamy koło do wycinka. */
export const MIN_SPIN = 30;

/** Prędkość puszczenia palca (°/s) z ostatnich ~120 ms ruchu; 0, gdy palec stał albo próbek za mało. */
export function releaseVelocity(samples: readonly AngleSample[], windowMs = 120): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1] as AngleSample;
  const recent = samples.filter((s) => last.t - s.t <= windowMs);
  const first = recent[0] as AngleSample;
  const dt = last.t - first.t;
  if (recent.length < 2 || dt < 8) return 0;
  const v = ((last.a - first.a) / dt) * 1000;
  return Math.max(-MAX_SPIN, Math.min(MAX_SPIN, v));
}

/** Jeden krok rozpędu: nowy obrót i prędkość po `dtMs` (prędkość 0 = rozpęd wygasł). */
export function stepSpin(angle: number, v: number, dtMs: number, friction = FRICTION): { angle: number; v: number } {
  const dt = Math.max(0, dtMs) / 1000;
  const nv = v * Math.exp(-friction * dt);
  return { angle: angle + ((v + nv) / 2) * dt, v: Math.abs(nv) < MIN_SPIN ? 0 : nv };
}

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - Math.max(0, Math.min(1, t)), 3);

/** Czas dosunięcia lub przejazdu do wycinka: krótki dla małej poprawki, dłuższy dla dotknięcia odległego wycinka (220–700 ms). */
export const travelMs = (delta: number): number => Math.round(Math.max(220, Math.min(700, 220 + Math.abs(delta) * 3)));

/** Następny wycinek w kierunku `dir` (+1 = zgodnie z ruchem wskazówek zegara, −1 = przeciwnie), z zawinięciem. */
export const stepIndex = (index: number, dir: 1 | -1, n: number): number => mod(index + dir, n);
